/**
 * Income Management Module
 * 
 * Handles advanced income tracking, forecasting, and allocation
 * Features:
 * - Multiple income source types and configurations
 * - Percentage-based allocation system
 * - Income forecasting and analytics
 * - Integration with budget and expense tracking
 */

import { formatCurrency } from './utils/currency.js';
import { setSynced, setSyncError, setSyncing } from './utils/sync.js';
import { collection, doc, setDoc, getDoc, getDocs, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from './firebase.js';
import { getCurrentUser } from './auth.js';

// Income source types
export const INCOME_TYPES = {
  SALARY: 'salary',
  FREELANCE: 'freelance',
  BUSINESS: 'business',
  INVESTMENT: 'investment',
  SIDE_HUSTLE: 'side_hustle',
  BENEFITS: 'benefits',
  OTHER: 'other'
};

// Income frequencies
export const INCOME_FREQUENCIES = {
  WEEKLY: 'weekly',
  BI_WEEKLY: 'bi_weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  ANNUALLY: 'annually',
  IRREGULAR: 'irregular'
};

// Income source class for managing individual income sources
class IncomeSource {
  constructor(data = {}) {
    this.id = data.id || this.generateId();
    this.name = data.name || 'New Income Source';
    this.type = data.type || INCOME_TYPES.OTHER;
    this.frequency = data.frequency || INCOME_FREQUENCIES.MONTHLY;
    this.expectedAmount = data.expectedAmount || 0;
    this.isActive = data.isActive !== undefined ? data.isActive : true;
    this.isGrossIncome = data.isGrossIncome !== undefined ? data.isGrossIncome : true;
    this.taxWithholding = data.taxWithholding || 0;
    this.deductions = data.deductions || 0;
    this.startDate = data.startDate || new Date();
    this.endDate = data.endDate || null;
    this.description = data.description || '';
    this.paymentSchedule = data.paymentSchedule || {};
    this.amountRange = data.amountRange || null; // For variable income
    this.allocationRules = data.allocationRules || [];
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = new Date();
  }

  generateId() {
    return 'income_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Calculate net amount after taxes and deductions
  getNetAmount() {
    if (this.isGrossIncome) {
      return this.expectedAmount - (this.taxWithholding || 0) - (this.deductions || 0);
    }
    return this.expectedAmount;
  }

  // Get monthly equivalent amount
  getMonthlyAmount() {
    const multipliers = {
      [INCOME_FREQUENCIES.WEEKLY]: 4.33,
      [INCOME_FREQUENCIES.BI_WEEKLY]: 2.17,
      [INCOME_FREQUENCIES.MONTHLY]: 1,
      [INCOME_FREQUENCIES.QUARTERLY]: 1/3,
      [INCOME_FREQUENCIES.ANNUALLY]: 1/12,
      [INCOME_FREQUENCIES.IRREGULAR]: 1 // Use expected amount as-is for irregular
    };
    
    return this.getNetAmount() * (multipliers[this.frequency] || 1);
  }

  // Validate allocation rules
  validateAllocations() {
    const monthlyAmount = this.getMonthlyAmount();
    let totalAllocated = 0;
    
    for (const rule of this.allocationRules) {
      if (rule.method === 'percentage') {
        totalAllocated += (monthlyAmount * rule.value) / 100;
      } else if (rule.method === 'fixed_amount') {
        totalAllocated += rule.value;
      }
      // existing_expenses method doesn't count toward over-allocation since it's based on historical data
    }
    
    return totalAllocated <= monthlyAmount;
  }

  // Get allocation for a specific category
  getAllocationForCategory(categoryId) {
    return this.allocationRules.find(rule => rule.categoryId === categoryId);
  }

  // Update allocation rule for a category
  setAllocation(categoryId, value, method = 'percentage', priority = 0, isFixed = false) {
    const existingIndex = this.allocationRules.findIndex(rule => rule.categoryId === categoryId);
    
    const allocation = {
      categoryId,
      method, // 'percentage', 'fixed_amount', 'existing_expenses'
      value, // percentage (0-100), fixed dollar amount, or 'auto' for existing expenses
      priority,
      isFixed
    };
    
    if (existingIndex >= 0) {
      this.allocationRules[existingIndex] = allocation;
    } else {
      this.allocationRules.push(allocation);
    }
    
    // Sort by priority
    this.allocationRules.sort((a, b) => a.priority - b.priority);
    this.updatedAt = new Date();
  }

  // Remove allocation for a category
  removeAllocation(categoryId) {
    this.allocationRules = this.allocationRules.filter(rule => rule.categoryId !== categoryId);
    this.updatedAt = new Date();
  }

  // Get unallocated amount (in dollars)
  getUnallocatedAmount() {
    const monthlyAmount = this.getMonthlyAmount();
    let totalAllocated = 0;
    
    for (const rule of this.allocationRules) {
      totalAllocated += this.calculateAllocationAmount(rule);
    }
    
    return Math.max(0, monthlyAmount - totalAllocated);
  }
  
  // Calculate allocation amount for a specific rule
  calculateAllocationAmount(rule, existingExpenseData = null) {
    const monthlyAmount = this.getMonthlyAmount();
    
    switch (rule.method) {
      case 'percentage':
        return (monthlyAmount * rule.value) / 100;
      case 'fixed_amount':
        return rule.value;
      case 'existing_expenses':
        // This would need to be provided from the budget data
        if (existingExpenseData && existingExpenseData[rule.categoryId]) {
          return existingExpenseData[rule.categoryId];
        }
        return 0;
      default:
        return 0;
    }
  }

  // Convert to plain object for storage
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      frequency: this.frequency,
      expectedAmount: this.expectedAmount,
      isActive: this.isActive,
      isGrossIncome: this.isGrossIncome,
      taxWithholding: this.taxWithholding,
      deductions: this.deductions,
      startDate: this.startDate,
      endDate: this.endDate,
      description: this.description,
      paymentSchedule: this.paymentSchedule,
      amountRange: this.amountRange,
      allocationRules: this.allocationRules,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }
}

// Income Manager class for handling multiple income sources
class IncomeManager {
  constructor() {
    this.incomeSources = new Map();
    this.incomeEntries = [];
    this.forecasts = new Map();
  }

  // Add or update an income source
  addIncomeSource(data) {
    const source = new IncomeSource(data);
    this.incomeSources.set(source.id, source);
    return source;
  }

  // Convenience method to add and save an income source in one operation
  async addAndSaveIncomeSource(data) {
    const source = this.addIncomeSource(data);
    await this.saveIncomeSource(source);
    return source;
  }

  // Get an income source by ID
  getIncomeSource(id) {
    return this.incomeSources.get(id);
  }

  // Get all active income sources
  getActiveIncomeSources() {
    return Array.from(this.incomeSources.values()).filter(source => source.isActive);
  }

  // Remove an income source
  removeIncomeSource(id) {
    return this.incomeSources.delete(id);
  }

  // Calculate total monthly income from all active sources
  getTotalMonthlyIncome() {
    return this.getActiveIncomeSources()
      .reduce((total, source) => total + source.getMonthlyAmount(), 0);
  }

  // Get income breakdown by source
  getIncomeBreakdown() {
    const breakdown = {};
    const totalIncome = this.getTotalMonthlyIncome();
    
    this.getActiveIncomeSources().forEach(source => {
      const monthlyAmount = source.getMonthlyAmount();
      breakdown[source.id] = {
        name: source.name,
        type: source.type,
        amount: monthlyAmount,
        percentage: totalIncome > 0 ? (monthlyAmount / totalIncome) * 100 : 0,
        source: source
      };
    });
    
    return breakdown;
  }

  // Get budget allocations from all income sources
  getBudgetAllocations() {
    const allocations = {};
    
    this.getActiveIncomeSources().forEach(source => {
      const monthlyAmount = source.getMonthlyAmount();
      
      source.allocationRules.forEach(rule => {
        if (!allocations[rule.categoryId]) {
          allocations[rule.categoryId] = {
            totalAmount: 0,
            sources: [],
            isFixed: false,
            priority: rule.priority
          };
        }
        
        const allocationAmount = (monthlyAmount * rule.percentage) / 100;
        allocations[rule.categoryId].totalAmount += allocationAmount;
        allocations[rule.categoryId].sources.push({
          sourceId: source.id,
          sourceName: source.name,
          percentage: rule.percentage,
          amount: allocationAmount
        });
        
        // Mark as fixed if any source marks it as fixed
        if (rule.isFixed) {
          allocations[rule.categoryId].isFixed = true;
        }
        
        // Use the highest priority
        allocations[rule.categoryId].priority = Math.min(
          allocations[rule.categoryId].priority, 
          rule.priority
        );
      });
    });
    
    return allocations;
  }

  // Generate income forecast for upcoming months
  generateForecast(months = 3) {
    const forecast = [];
    const today = new Date();
    
    for (let i = 0; i < months; i++) {
      const forecastDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const monthKey = forecastDate.toISOString().substr(0, 7); // YYYY-MM
      
      let totalPredicted = 0;
      const sourcePredictions = {};
      
      this.getActiveIncomeSources().forEach(source => {
        const prediction = this.predictIncomeForMonth(source, forecastDate);
        sourcePredictions[source.id] = prediction;
        totalPredicted += prediction.predictedAmount;
      });
      
      forecast.push({
        month: monthKey,
        date: forecastDate,
        totalPredicted,
        sourcePredictions,
        confidenceLevel: this.calculateOverallConfidence(sourcePredictions)
      });
    }
    
    return forecast;
  }

  // Predict income for a specific source and month
  predictIncomeForMonth(source, targetDate) {
    const monthlyAmount = source.getMonthlyAmount();
    
    // For now, use simple prediction based on frequency
    // TODO: Implement more sophisticated prediction with historical data
    let predictedAmount = monthlyAmount;
    let confidenceLevel = 90; // High confidence for regular income
    
    // Adjust confidence based on income type
    switch (source.type) {
      case INCOME_TYPES.SALARY:
        confidenceLevel = 95;
        break;
      case INCOME_TYPES.FREELANCE:
        confidenceLevel = 70;
        // Could vary ±20% for freelance
        predictedAmount = monthlyAmount;
        break;
      case INCOME_TYPES.BUSINESS:
        confidenceLevel = 60;
        break;
      case INCOME_TYPES.INVESTMENT:
        confidenceLevel = 50;
        break;
      default:
        confidenceLevel = 80;
    }
    
    return {
      predictedAmount,
      confidenceLevel,
      predictionRange: {
        low: predictedAmount * 0.8,
        medium: predictedAmount,
        high: predictedAmount * 1.2
      }
    };
  }

  // Calculate overall confidence from source predictions
  calculateOverallConfidence(sourcePredictions) {
    const sources = Object.values(sourcePredictions);
    if (sources.length === 0) return 0;
    
    const weightedConfidence = sources.reduce((sum, pred, index) => {
      return sum + pred.confidenceLevel;
    }, 0);
    
    return Math.round(weightedConfidence / sources.length);
  }

  // Load income data from Firestore
  async loadData() {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.log('No user logged in, skipping income data load');
      return;
    }

    try {
      setSyncing('Loading income sources...');
      
      // Check for existing localStorage data to migrate
      await this.migrateFromLocalStorage();
      
      // Load from Firestore
      const incomeSourcesRef = collection(db, `users/${currentUser.uid}/incomeSources`);
      const q = query(incomeSourcesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      // Clear existing sources first
      this.incomeSources.clear();
      
      // Add each source from Firestore
      querySnapshot.forEach(doc => {
        const sourceData = { id: doc.id, ...doc.data() };
        this.addIncomeSource(sourceData);
      });
      
      console.log(`Loaded ${this.incomeSources.size} income sources from Firestore`);
      setSynced('Income sources loaded');
    } catch (error) {
      console.error('Error loading income data:', error);
      setSyncError('Failed to load income sources');
    }
  }

  // Save all income data to Firestore
  async saveData() {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.log('No user logged in, skipping income data save');
      return;
    }

    try {
      setSyncing('Saving income sources...');
      
      // Save all income sources to Firestore
      const savePromises = Array.from(this.incomeSources.values()).map(source => 
        this.saveIncomeSource(source)
      );
      
      await Promise.all(savePromises);
      setSynced('Income sources saved successfully');
    } catch (error) {
      console.error('Error saving income data:', error);
      setSyncError('Error saving income sources');
    }
  }

  // Save individual income source to Firestore
  async saveIncomeSource(source) {
    const currentUser = getCurrentUser();
    if (!currentUser) return;

    try {
      const sourceRef = doc(db, `users/${currentUser.uid}/incomeSources`, source.id);
      const sourceData = {
        ...source.toJSON(),
        lastModified: new Date(),
        updatedAt: new Date()
      };
      
      await setDoc(sourceRef, sourceData);
      return true;
    } catch (error) {
      console.error(`Error saving income source ${source.id}:`, error);
      throw error;
    }
  }

  // Delete income source from Firestore
  async deleteIncomeSource(sourceId) {
    const currentUser = getCurrentUser();
    if (!currentUser) return false;

    try {
      // Remove from local memory
      const success = this.removeIncomeSource(sourceId);
      
      if (success) {
        // Remove from Firestore
        const sourceRef = doc(db, `users/${currentUser.uid}/incomeSources`, sourceId);
        await deleteDoc(sourceRef);
        setSynced('Income source deleted');
        return true;
      }
      return false;
    } catch (error) {
      console.error(`Error deleting income source ${sourceId}:`, error);
      setSyncError('Failed to delete income source');
      return false;
    }
  }

  // Migrate existing localStorage data to Firestore (one-time operation)
  async migrateFromLocalStorage() {
    try {
      const storedData = localStorage.getItem('budgetapp_income_sources');
      if (!storedData) return; // No data to migrate

      console.log('Found localStorage income data, migrating to Firestore...');
      const incomeSources = JSON.parse(storedData);
      
      if (incomeSources.length === 0) return;

      // Add each source from localStorage
      for (const sourceData of incomeSources) {
        // Ensure the source has required fields
        if (!sourceData.createdAt) {
          sourceData.createdAt = new Date();
        }
        if (!sourceData.id) {
          sourceData.id = `income_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }

        // Add to memory and save to Firestore
        const source = this.addIncomeSource(sourceData);
        await this.saveIncomeSource(source);
      }

      console.log(`Migrated ${incomeSources.length} income sources to Firestore`);
      
      // Clear localStorage after successful migration
      localStorage.removeItem('budgetapp_income_sources');
      console.log('Cleared localStorage after migration');

    } catch (error) {
      console.error('Error migrating income data:', error);
      // Don't throw - this is not critical for app functionality
    }
  }

  // Convert to plain object for storage
  toJSON() {
    return {
      incomeSources: Array.from(this.incomeSources.values()).map(source => source.toJSON()),
      incomeEntries: this.incomeEntries,
      lastUpdated: new Date()
    };
  }
}

// Global income manager instance
export const incomeManager = new IncomeManager();

// Clear income data for user switching
export function clearIncomeData() {
  console.log('Clearing income data for user switch');
  incomeManager.incomeSources.clear();
  incomeManager.incomeEntries = [];
  incomeManager.forecasts.clear();
  console.log('Income data cleared');
}

// Initialize income management
export async function initializeIncomeManagement() {
  await incomeManager.loadData();
  console.log('Income management initialized');
}

// Utility functions for UI integration

// Get formatted income type display name
export function getIncomeTypeDisplayName(type) {
  const displayNames = {
    [INCOME_TYPES.SALARY]: 'Salary/Wages',
    [INCOME_TYPES.FREELANCE]: 'Freelance/Contract',
    [INCOME_TYPES.BUSINESS]: 'Business Income',
    [INCOME_TYPES.INVESTMENT]: 'Investment Income',
    [INCOME_TYPES.SIDE_HUSTLE]: 'Side Hustle',
    [INCOME_TYPES.BENEFITS]: 'Government Benefits',
    [INCOME_TYPES.OTHER]: 'Other Income'
  };
  return displayNames[type] || type;
}

// Get formatted frequency display name
export function getFrequencyDisplayName(frequency) {
  const displayNames = {
    [INCOME_FREQUENCIES.WEEKLY]: 'Weekly',
    [INCOME_FREQUENCIES.BI_WEEKLY]: 'Bi-weekly',
    [INCOME_FREQUENCIES.MONTHLY]: 'Monthly',
    [INCOME_FREQUENCIES.QUARTERLY]: 'Quarterly',
    [INCOME_FREQUENCIES.ANNUALLY]: 'Annually',
    [INCOME_FREQUENCIES.IRREGULAR]: 'Irregular'
  };
  return displayNames[frequency] || frequency;
}

// Create income source options HTML
export function createIncomeTypeOptions() {
  return Object.values(INCOME_TYPES).map(type => 
    `<option value="${type}">${getIncomeTypeDisplayName(type)}</option>`
  ).join('');
}

// Create frequency options HTML
export function createFrequencyOptions() {
  return Object.values(INCOME_FREQUENCIES).map(frequency => 
    `<option value="${frequency}">${getFrequencyDisplayName(frequency)}</option>`
  ).join('');
}

// Export the classes for external use
export { IncomeSource, IncomeManager };